/**
 * Agent Route Component
 *
 * Redirects unauthenticated users to /login.
 * Redirects account_manager-only users to /am.
 * Allows admins and regular agents through.
 * Allows admins with ?viewAs={agentId} through (Phase D).
 */

import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";

interface AgentRouteProps {
  children: React.ReactNode;
}

export function AgentRoute({ children }: AgentRouteProps) {
  const { session, loading: authLoading } = useAuth();
  const { role, loading: adminLoading } = useAdmin();
  const [searchParams] = useSearchParams();

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Admin with viewAs param can access /dashboard/* routes
  const viewAsAgentId = searchParams.get("viewAs");
  if (role === "admin" && viewAsAgentId) {
    return <>{children}</>;
  }

  if (role === "account_manager") {
    return <Navigate to="/am" replace />;
  }

  return <>{children}</>;
}
